// src/components/PlayArrived.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ThumbUpRoundedIcon from '@mui/icons-material/ThumbUpRounded';
import ThumbDownRoundedIcon from '@mui/icons-material/ThumbDownRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import SectionCard from '../../components/ui/SectionCard';
import EmptyState from '../../components/ui/EmptyState';
import PlayerAvatar from '../../components/ui/PlayerAvatar';
import formatDateToBrazilTime from '../../utils/dateUtils';
import { generateWhatsAppLink } from '../../utils/shareUtils';
import tokens from '../../theme/tokens';
import { marcarArraste } from '../../utils/tempoReal';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import AssignmentReturnRoundedIcon from '@mui/icons-material/AssignmentReturnRounded';
import LockClockRoundedIcon from '@mui/icons-material/LockClockRounded';
import NfcRoundedIcon from '@mui/icons-material/NfcRounded';

// Hora "HH:mm" de Brasília
const horaBrasilia = (date) => new Date(date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });

/** Relógio que atualiza a cada 30 s (libera o botão "Chegou" na hora certa sem recarregar a tela). */
function useAgora() {
  const [agora, setAgora] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setAgora(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  return agora;
}

const hora = (date) => (date ? formatDateToBrazilTime(date).substring(11, 16) : '--:--');

const PlayerTags = ({ player }) => (
  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
    {player.guest && <Chip size="small" label="Convidado" variant="outlined" />}
    {player.typePlayer === 'D' && !player.guest && <Chip size="small" label="Diarista" variant="outlined" color="info" />}
    {player.devedor && <Chip size="small" label="Em atraso" color="error" variant="outlined" />}
  </Stack>
);

const porNome = (a, b) => (a.user_name || '').localeCompare(b.user_name || '', 'pt-BR', { sensitivity: 'base' });

const BotaoPresenca = ({ vai, onClick, disabled }) => (
  <Tooltip title={vai ? 'Confirmar presença' : 'Marcar que não vai'}>
    <span>
      <IconButton size="small" onClick={onClick} disabled={disabled} sx={{ color: vai ? '#10B981' : '#EF4444' }}>
        {vai ? <ThumbUpRoundedIcon fontSize="small" /> : <ThumbDownRoundedIcon fontSize="small" />}
      </IconButton>
    </span>
  </Tooltip>
);

// Lista compacta em ordem alfabética (não confirmaram / não vão), com busca e ações da diretoria.
const ListaPresenca = ({ titulo, vazio, jogadores, ehDiretoria, acoes, loading }) => {
  const [busca, setBusca] = useState('');
  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return [...jogadores].sort(porNome).filter((p) => !termo || (p.user_name || '').toLowerCase().includes(termo));
  }, [jogadores, busca]);

  return (
    <SectionCard title={titulo} subtitle={`${jogadores.length} jogador${jogadores.length === 1 ? '' : 'es'} · ordem alfabética`} sx={{ height: '100%' }}>
      {jogadores.length === 0 ? (
        <EmptyState title={vazio} />
      ) : (
        <>
          {jogadores.length > 8 && (
            <TextField
              size="small"
              fullWidth
              placeholder="Buscar jogador"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              sx={{ mb: 1.5 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
            />
          )}
          <Stack spacing={0.5} sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
            {lista.map((player) => (
              <Stack key={String(player.user)} direction="row" spacing={1.25} alignItems="center" sx={{ py: 0.75, px: 1, borderRadius: 2, '&:hover': { bgcolor: 'action.hover' } }}>
                <PlayerAvatar userId={player.guest ? null : player.user} name={player.user_name} size={32} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{player.user_name}</Typography>
                  <PlayerTags player={player} />
                </Box>
                {ehDiretoria && acoes(player)}
              </Stack>
            ))}
            {lista.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>Nenhum jogador encontrado.</Typography>}
          </Stack>
        </>
      )}
    </SectionCard>
  );
};

/**
 * Arrastar para reordenar (mouse e toque) pela alça do cartão.
 * - Os eventos são ouvidos na janela: o arraste não se perde se o cartão sair de baixo do dedo.
 * - A lista não se mexe durante o arraste (evita "pulos"); uma linha mostra onde o cartão vai cair.
 * - Perto da borda da tela a página rola sozinha (dá para levar o 2º até o fim da fila).
 * - Ao soltar, a nova ordem aparece na hora e a API confirma em segundo plano; se falhar, volta.
 * `onSoltar(item, novaPosicao)` deve devolver true/false (sucesso na API).
 */
function useArrastarFila(itens, onSoltar) {
  const [arraste, setArraste] = useState(null); // { id, nome, x, y, destino }
  const [ordemOtimista, setOrdemOtimista] = useState(null); // ids na nova ordem enquanto a API confirma
  const elementos = useRef({});
  const atual = useRef(null);
  atual.current = arraste;
  const arrastando = Boolean(arraste);

  // Chegaram dados novos da API: vale a ordem do servidor
  useEffect(() => setOrdemOtimista(null), [itens]);

  const ordenados = ordemOtimista ? ordemOtimista.map((id) => itens.find((p) => p._id === id)).filter(Boolean) : itens;
  const ordenadosRef = useRef(ordenados);
  ordenadosRef.current = ordenados;

  // Posição final = quantos cartões (sem o arrastado) têm o centro acima do dedo
  const destinoEm = (y, id) =>
    ordenadosRef.current.filter((p) => {
      if (p._id === id) return false;
      const el = elementos.current[p._id];
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return r.top + r.height / 2 < y;
    }).length;

  // Tempo real: segura as atualizações enquanto o card está sendo arrastado
  useEffect(() => {
    marcarArraste(arrastando);
    return () => { if (arrastando) marcarArraste(false); };
  }, [arrastando]);

  useEffect(() => {
    if (!arrastando) return undefined;
    const BORDA = 90;
    let velocidade = 0;
    let ultimoY = null;
    const rolagem = setInterval(() => {
      if (!velocidade) return;
      window.scrollBy(0, velocidade);
      // com o dedo parado e a página rolando, a posição de destino muda
      if (ultimoY !== null) setArraste((a) => a && { ...a, destino: destinoEm(ultimoY, a.id) });
    }, 16);
    const mover = (e) => {
      ultimoY = e.clientY;
      if (e.clientY < BORDA) velocidade = -Math.ceil((BORDA - e.clientY) / 5);
      else if (e.clientY > window.innerHeight - BORDA) velocidade = Math.ceil((e.clientY - (window.innerHeight - BORDA)) / 5);
      else velocidade = 0;
      setArraste((a) => a && { ...a, x: e.clientX, y: e.clientY, destino: destinoEm(e.clientY, a.id) });
    };
    const soltar = (e) => {
      const a = atual.current;
      setArraste(null);
      if (!a) return;
      const lista = ordenadosRef.current;
      const origem = lista.findIndex((p) => p._id === a.id);
      const destino = destinoEm(e.clientY, a.id);
      if (origem === -1 || destino === origem) return;
      const nova = lista.map((p) => p._id).filter((id) => id !== a.id);
      nova.splice(destino, 0, a.id);
      setOrdemOtimista(nova);
      Promise.resolve(onSoltar(lista[origem], destino)).then((ok) => {
        if (ok === false) setOrdemOtimista(null);
      });
    };
    const cancelar = () => setArraste(null);
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', cancelar);
    return () => {
      clearInterval(rolagem);
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', cancelar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrastando]);

  const iniciar = (e, player) => {
    e.preventDefault();
    const origem = ordenadosRef.current.findIndex((p) => p._id === player._id);
    setArraste({ id: player._id, nome: player.guest ? player.guestName : player.user_name, x: e.clientX, y: e.clientY, destino: origem });
  };

  const alca = (player) => ({ onPointerDown: (e) => iniciar(e, player) });
  const registrar = (id) => (el) => { elementos.current[id] = el; };

  return { ordenados, arraste, alca, registrar };
}

// Linha que mostra onde o cartão arrastado vai cair
const MarcaDestino = () => (
  <Box sx={{ height: 4, borderRadius: 2, bgcolor: tokens.color.brand, boxShadow: `0 0 0 3px ${tokens.color.brandSoft}` }} />
);

const PlayArrived = ({ confirmedPlayers, arrivedPlayers, undecidedPlayers = [], declinedPlayers = [], playArrived, removePlayer, restorePlayer, desfazerChegada, meuId, onMover, play, loading, ehDiretoria, onAlterarPresenca }) => {
  const { ordenados, arraste, alca, registrar } = useArrastarFila(arrivedPlayers, onMover);
  const agora = useAgora();
  // Botão "Chegou" só a partir do horário de abertura do check-in (padrão 19:00); a diretoria registra antes
  const checkinAbreEm = play && play.checkinAbreEm ? new Date(play.checkinAbreEm) : null;
  const checkinFechado = !ehDiretoria && checkinAbreEm && agora < checkinAbreEm.getTime();
  // Botão "Chegou" desligado nas Configurações ou pelada que já teve check-in pela tag: para os jogadores vira
  // o aviso "Check-in por NFC"; a diretoria continua com o botão (jogador sem celular)
  const somenteNfc = !ehDiretoria && play && play.checkinBotaoLiberado === false;
  // Desfazer check-in: o próprio jogador (ou anfitrião do convidado) e a diretoria
  const [desfazer, setDesfazer] = useState(null);
  const podeDesfazer = (player) => !player.saiu && (ehDiretoria || String(player.user) === String(meuId));
  const arrastandoId = arraste && arraste.id;
  // Índice (na lista sem o arrastado) antes do qual aparece a linha de destino
  const idxArrastado = arraste ? ordenados.findIndex((p) => p._id === arraste.id) : -1;
  const destinoMostrado = arraste && arraste.destino !== idxArrastado ? arraste.destino : null;

  const payloadFor = (player) => {
    const { user, guest, guestId } = player;
    const peladaId = play._id;
    return guest ? { userId: user, peladaId, guestId: guestId } : { userId: user, peladaId };
  };

  const handleArrival = (player) => playArrived(payloadFor(player), player);
  const handleOut = (player) => removePlayer(payloadFor(player));
  const handleBack = (player) => restorePlayer(payloadFor(player));

  // Quem foi embora continua no lugar (check-in vale para as estatísticas), mas sem posição na fila
  const naFila = arrivedPlayers.filter((p) => !p.saiu);
  // Quem já fez check-in vê primeiro a concentração (no celular, acima de Confirmados; no computador, à esquerda)
  const euCheguei = arrivedPlayers.some((p) => !p.guestId && String(p.user) === String(meuId));
  let posicao = 0;
  const concentracao = ordenados.map((player) => ({ player, posicao: player.saiu ? null : ++posicao }));

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={5}>
        <SectionCard
          title="Confirmados"
          subtitle={`${confirmedPlayers.length} aguardando chegada${checkinAbreEm ? ` · check-in a partir das ${horaBrasilia(checkinAbreEm)}` : ''}`}
        >
          {confirmedPlayers.length === 0 ? (
            <EmptyState title="Ninguém aguardando" description="Todos os confirmados já chegaram." />
          ) : (
            <Stack spacing={1}>
              {confirmedPlayers.map(player => (
                <Stack key={player._id} direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.25, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                  <PlayerAvatar userId={player.guest ? null : player.user} name={player.guest ? player.guestName : player.user_name} size={40} />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography fontWeight={600} noWrap>{player.user_name}</Typography>
                    <Typography variant="caption" color="text.secondary">Confirmou às {hora(player.confirmationTime)}</Typography>
                    <PlayerTags player={player} />
                  </Box>
                  {ehDiretoria && !player.guest && (
                    <BotaoPresenca vai={false} onClick={() => onAlterarPresenca(player, false)} disabled={loading} />
                  )}
                  {somenteNfc ? (
                    <Tooltip title="Encoste o celular na tag NFC da concentração para fazer o check-in">
                      <Chip size="small" variant="outlined" icon={<NfcRoundedIcon />} label="Check-in por NFC" sx={{ flexShrink: 0 }} />
                    </Tooltip>
                  ) : (
                    <Tooltip title={checkinFechado ? `O check-in abre às ${horaBrasilia(checkinAbreEm)}` : ''}>
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={checkinFechado ? <LockClockRoundedIcon /> : <CheckRoundedIcon />}
                          onClick={() => handleArrival(player)}
                          disabled={loading || checkinFechado}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {checkinFechado ? horaBrasilia(checkinAbreEm) : 'Chegou'}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </SectionCard>
      </Grid>
      <Grid item xs={12} md={7} sx={{ order: euCheguei ? -1 : 0 }}>
        <SectionCard
          title="Na concentração"
          subtitle={`${naFila.length} na fila · ordem por prioridade${naFila.length !== arrivedPlayers.length ? ` · ${arrivedPlayers.length - naFila.length} foram embora` : ''}`}
          actions={
            <Button size="small" variant="outlined" color="success" startIcon={<WhatsAppIcon />} href={generateWhatsAppLink(naFila)} target="_blank" rel="noopener noreferrer">
              Compartilhar
            </Button>
          }
        >
          {arrivedPlayers.length === 0 ? (
            <EmptyState title="Ninguém chegou ainda" description="A lista é ordenada por mensalista, adimplência e horário de chegada. Quem chega às 20h ou depois (horário de Brasília) vai para o fim da fila." />
          ) : (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Arraste pela alça <DragIndicatorRoundedIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> para mudar a posição. Cartões em azul mudaram de colocação.
              </Typography>
              {concentracao.map(({ player, posicao: pos }, idx) => {
                const destaque = pos !== null && pos <= 8;
                const arrastando = arrastandoId === player._id;
                const alterado = player.alterado && !player.saiu;
                const indiceSemArrastado = idxArrastado >= 0 && idx > idxArrastado ? idx - 1 : idx;
                const marcaAntes = destinoMostrado !== null && !arrastando && indiceSemArrastado === destinoMostrado;
                const ultimo = idx === concentracao.length - 1;
                // destino = fim da fila: a linha aparece depois do último cartão (que não é o arrastado)
                const marcaDepois = destinoMostrado !== null && ultimo && !arrastando && destinoMostrado === concentracao.length - 1;
                return (
                  <React.Fragment key={player._id}>
                  {marcaAntes && <MarcaDestino />}
                  <Stack
                    ref={registrar(player._id)}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      p: 1.25,
                      pl: 0.5,
                      borderRadius: 2,
                      border: 1,
                      borderStyle: player.saiu ? 'dashed' : 'solid',
                      borderColor: alterado ? '#90CAF9' : destaque ? tokens.color.brandSoft : 'divider',
                      bgcolor: alterado ? '#E3F2FD' : destaque ? '#FFF8F9' : 'transparent',
                      opacity: player.saiu ? 0.6 : arrastando ? 0.35 : 1,
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                    }}
                  >
                    <Box
                      {...(player.saiu ? {} : alca(player))}
                      aria-label="Arrastar para mudar a posição"
                      sx={{ display: 'grid', placeItems: 'center', alignSelf: 'stretch', minWidth: 40, mx: -0.5, touchAction: 'none', cursor: player.saiu ? 'default' : arrastando ? 'grabbing' : 'grab', color: player.saiu ? 'transparent' : 'text.secondary' }}
                    >
                      <DragIndicatorRoundedIcon />
                    </Box>
                    <Box sx={{ width: 32, textAlign: 'center', fontFamily: tokens.font.display, fontWeight: 700, fontSize: '1.4rem', color: destaque ? tokens.color.brand : tokens.color.textMuted }}>
                      {pos === null ? '–' : pos}
                    </Box>
                    <PlayerAvatar userId={player.guest ? null : player.user} name={player.guest ? player.guestName : player.user_name} size={40} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography fontWeight={600} noWrap>{player.user_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Confirmou {hora(player.confirmationTime)} · chegou {hora(player.arrivalTime)}
                        {player.saiu ? ` · saiu ${hora(player.saiuEm)}` : ''}
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {player.saiu && <Chip size="small" label="Foi embora" color="default" />}
                        {player.atrasado && <Chip size="small" label="Chegou após 20h" color="warning" variant="outlined" />}
                        <PlayerTags player={player} />
                      </Stack>
                    </Box>
                    {player.saiu ? (
                      <Tooltip title="Voltar para a fila (mesma posição)">
                        <span>
                          <Button size="small" variant="outlined" startIcon={<UndoRoundedIcon />} onClick={() => handleBack(player)} disabled={loading}>
                            Voltar
                          </Button>
                        </span>
                      </Tooltip>
                    ) : (
                      <Stack direction="row" spacing={0}>
                        {podeDesfazer(player) && (
                          <Tooltip title="Desfazer check-in (apertou Chegou por engano): volta para Confirmados">
                            <span>
                              <IconButton color="primary" onClick={() => setDesfazer(player)} disabled={loading}>
                                <AssignmentReturnRoundedIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Foi embora (sai da fila de times; o check-in continua valendo)">
                          <span>
                            <IconButton color="error" onClick={() => handleOut(player)} disabled={loading}>
                              <LogoutRoundedIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>
                  {marcaDepois && <MarcaDestino />}
                  </React.Fragment>
                );
              })}
            </Stack>
          )}
          <Dialog open={Boolean(desfazer)} onClose={() => setDesfazer(null)}>
            <DialogTitle>Desfazer o check-in?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {desfazer ? (desfazer.guest ? desfazer.guestName || desfazer.user_name : desfazer.user_name) : ''} sai da concentração e volta para
                Confirmados. Se houve cobrança de diária neste check-in, ela é estornada. Ao chegar de verdade, é só apertar
                "Chegou" de novo (a posição na fila será a do novo horário).
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setDesfazer(null)} color="inherit">Cancelar</Button>
              <Button
                variant="contained"
                disabled={loading}
                onClick={async () => {
                  const player = desfazer;
                  setDesfazer(null);
                  await desfazerChegada(payloadFor(player));
                }}
              >
                Desfazer check-in
              </Button>
            </DialogActions>
          </Dialog>
          {arraste && (
            // Cartão "fantasma" que acompanha o dedo durante o arraste
            <Chip
              icon={<DragIndicatorRoundedIcon />}
              label={arraste.nome}
              color="primary"
              sx={{ position: 'fixed', left: Math.max(8, arraste.x - 60), top: arraste.y - 44, zIndex: 2000, pointerEvents: 'none', boxShadow: 6, maxWidth: 260 }}
            />
          )}
        </SectionCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <ListaPresenca
          titulo="Não confirmaram"
          vazio="Todos já responderam"
          jogadores={undecidedPlayers}
          ehDiretoria={ehDiretoria}
          loading={loading}
          acoes={(player) => (
            <>
              <BotaoPresenca vai={false} onClick={() => onAlterarPresenca(player, false)} disabled={loading} />
              <BotaoPresenca vai onClick={() => onAlterarPresenca(player, true)} disabled={loading} />
            </>
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <ListaPresenca
          titulo="Não vão"
          vazio="Ninguém recusou"
          jogadores={declinedPlayers}
          ehDiretoria={ehDiretoria}
          loading={loading}
          acoes={(player) => <BotaoPresenca vai onClick={() => onAlterarPresenca(player, true)} disabled={loading} />}
        />
      </Grid>
    </Grid>
  );
};

export default PlayArrived;
